import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users } from 'lucide-react'
import { Card, CardContent } from '@/components/ui'
import {
  WizardProgress,
  FamilyNameStep,
  EmployerCodeStep,
  InviteMembersStep,
  type InviteMember,
} from '@/components/onboarding'
import { useFamily } from '@/hooks'
import { entitlementsApi, familyApi } from '@/lib/api'
import type { Permission } from '@/types'

type WizardStep = 'name' | 'employer' | 'invite'

const STEPS: WizardStep[] = ['name', 'employer', 'invite']

export function CreateFamilyWizard() {
  const navigate = useNavigate()
  const { createFamily, isCreating, setSelectedFamilyId } = useFamily()

  const [currentStep, setCurrentStep] = useState<WizardStep>('name')
  const [familyName, setFamilyName] = useState('')
  const [employerCode, setEmployerCode] = useState('')
  const [employerValidation, setEmployerValidation] = useState<{
    valid: boolean
    message?: string
    employerName?: string
  } | null>(null)
  const [invites, setInvites] = useState<InviteMember[]>([])
  const [createdFamilyId, setCreatedFamilyId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currentStepIndex = STEPS.indexOf(currentStep)

  const goNext = () => {
    const nextIndex = currentStepIndex + 1
    if (nextIndex < STEPS.length) {
      setCurrentStep(STEPS[nextIndex])
    }
  }
  const goBack = () => {
    const prevIndex = currentStepIndex - 1
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex])
    }
  }

  // Track validation timeout for debouncing
  const validationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isValidating, setIsValidating] = useState(false)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [])

  // Validate employer code using the real API
  const validateEmployerCode = async (code: string) => {
    if (!code.trim()) {
      setEmployerValidation(null)
      return
    }

    setIsValidating(true)
    try {
      const result = await entitlementsApi.validateCode(code.trim())
      setEmployerValidation({
        valid: result.valid,
        employerName: result.employer_name,
        message: result.valid ? undefined : (result.message ?? 'Invalid employer code'),
      })
    } catch {
      setEmployerValidation({
        valid: false,
        message: 'Unable to validate code. Please try again.',
      })
    } finally {
      setIsValidating(false)
    }
  }

  const handleEmployerCodeChange = (code: string) => {
    setEmployerCode(code)
    setEmployerValidation(null)

    // Clear any pending validation
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }

    // Debounce validation (wait 500ms after user stops typing)
    if (code.trim()) {
      validationTimeoutRef.current = setTimeout(() => {
        validateEmployerCode(code)
      }, 500)
    }
  }

  // Get default permissions based on role
  const getDefaultPermissions = (role: string): Permission[] => {
    switch (role) {
      case 'CAREGIVER':
        return ['view_health', 'view_financial']
      case 'PARENT':
        return ['view_health', 'view_financial']
      case 'VIEWER':
        return ['view_health']
      default:
        return ['view_health']
    }
  }

  const handleCreateFamily = async () => {
    setIsSubmitting(true)
    setError(null)

    try {
      // Create the family
      const family = await createFamily({
        name: familyName.trim(),
        // Note: employer_code would be passed here when backend supports it
      })

      setCreatedFamilyId(family.id)
      setSelectedFamilyId(family.id)

      // Send invitations if any
      if (invites.length > 0) {
        // Send invitations one by one (don't fail the whole process if one fails)
        const invitePromises = invites.map(async (invite) => {
          try {
            await familyApi.inviteMember(family.id, {
              email: invite.email,
              role: invite.role,
              permissions: getDefaultPermissions(invite.role),
            })
          } catch (err) {
            console.error(`Failed to send invitation to ${invite.email}:`, err)
            // Continue with other invitations even if one fails
          }
        })
        await Promise.all(invitePromises)
      }

      // Navigate to dashboard
      navigate('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create family')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFinish = async () => {
    if (!createdFamilyId) {
      // Need to create family first
      await handleCreateFamily()
    } else {
      navigate('/')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-lg px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
            <Users className="h-8 w-8 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Let's build your care circle</h1>
          <p className="mt-1 text-gray-500">
            Just a few quick steps and you'll be all set
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <WizardProgress currentStep={currentStepIndex} totalSteps={STEPS.length} />
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-4 rounded-md bg-error-50 p-4 text-sm text-error-700">
            {error}
          </div>
        )}

        {/* Wizard content */}
        <Card>
          <CardContent className="p-6">
            {currentStep === 'name' && (
              <FamilyNameStep
                familyName={familyName}
                onFamilyNameChange={setFamilyName}
                onNext={goNext}
                isValid={familyName.trim().length >= 2}
              />
            )}

            {currentStep === 'employer' && (
              <EmployerCodeStep
                employerCode={employerCode}
                onEmployerCodeChange={handleEmployerCodeChange}
                onNext={goNext}
                onBack={goBack}
                onSkip={goNext}
                isValidating={isValidating}
                validationResult={employerValidation}
              />
            )}

            {currentStep === 'invite' && (
              <InviteMembersStep
                invites={invites}
                onInvitesChange={setInvites}
                onNext={handleFinish}
                onBack={goBack}
                onSkip={handleFinish}
                isLoading={isSubmitting || isCreating}
              />
            )}
          </CardContent>
        </Card>

        {/* Cancel link */}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => navigate('/onboarding')}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel and go back
          </button>
        </div>
      </div>
    </div>
  )
}
